//#region Util Imports

import * as _ from "underscore";
import * as $ from "jquery";

//#endregion

//#region DevExpress Imports

import DevExpress from "devextreme";
import { IMRBaseControlController } from "../../../modules/common/base";
import { IIMRPopup, IMRPopupOptions } from "@imr/controls.angularjs/components/overlay/popup";
import { UserSessionService } from "../../../services/user-session";
import { IMRComponentInitializedArgs } from "@imr/controls.angularjs/components/base";
import { LocalizationHelper } from "../../../helpers";
import { IIMRLocationSchemaAssignPopup, IMRILocationSchemaAssignPopupOptions } from "./location-schema-assign-popup.interfaces";
import { OpData, OpDescr, OpProfileData } from "../../../models/core";
import { Enums } from "../../../models/common";
import { DataProviderRequest } from "app/data-provider";

//#endregion

export const COMPONENT_NAME: string =
    "locationSchemaAssignPopup";

export class LocationSchemaAssignPopupController
    extends IMRBaseControlController<
        IIMRLocationSchemaAssignPopup,
        IMRILocationSchemaAssignPopupOptions
    >
    implements IIMRLocationSchemaAssignPopup {

    private imrPopupOptions: IMRPopupOptions
    private imrPopup: IIMRPopup
    private isLoading: boolean
    private idLocation: number

    private listOptions: DevExpress.ui.dxListOptions
    private listDataSource: { value: string, key: (OpProfileData | OpData) }[]
    private selectedItemKeys: (OpProfileData | OpData)[]
    private schemas: [OpProfileData[], OpData[]]
    private submitButton: DevExpress.ui.dxButton

    private dataSourcePrepared: boolean = false

    constructor(
        private userSession: UserSessionService,
    ) {
        super();
    }

    //#region Component

    protected initialize(options: IMRILocationSchemaAssignPopupOptions) {
        return this.setOptions();
    }

    //#endregion

    private setOptions(): JQueryPromise<any> {
        this.setListOptions()
        return this.setImrPopupOption()
    }

    private onSubmit() {
        this.isLoading = true
        const dataToSave: [OpProfileData[], OpData[]] = [[], []]

        _.forEach(this.selectedItemKeys, (key: OpProfileData | OpData) => {
            if (key instanceof (OpProfileData)) {
                const data: OpProfileData = _.find(this.schemas[0], (value: OpProfileData) => value instanceof (OpProfileData) && value.indexNbr == key.indexNbr)

                if (data != null) {
                    data.opState = Enums.OpChangeState.New
                    dataToSave[0].push(data)
                }
            }
        })

        _.forEach(this.schemas[1], (value: OpData) => {
            if (value instanceof (OpData)) {
                const data: OpData | OpProfileData = _.find(this.selectedItemKeys, (key: OpData) => key instanceof (OpData) && value.indexNbr == key.indexNbr)

                if (data == null) {
                    value.opState = Enums.OpChangeState.Delete
                    dataToSave[1].push(value)
                }
            }
        })

        if (this.submitButton != null) {
            this.submitButton.option('disabled', true)
        }

        this.assignSchemasToLocation(this.idLocation, dataToSave[0], dataToSave[1])
            .then(() => {
                this.imrPopup.hide()

                if (_.isFunction(this.options.onSubmited)) {
                    this.options.onSubmited()
                }
            })
            .always(() => {
                this.isLoading = false
            })
    }

    private setImrPopupOption(): JQueryPromise<any> {
        this.imrPopupOptions = {
            innerOptions: {
                maxWidth: 700,
                height: 450,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AssignRemoveLocationSchema),
                onHiding: (e: any): void => {
                    this.reset()

                    if (this.submitButton != null) {
                        this.submitButton.option('disabled', true)
                    }
                }
            },
            onInitialized: (e: IMRComponentInitializedArgs<IIMRPopup>) => {
                this.imrPopup = e.component
            },
            bottomToolbarOptions: {
                items: [{
                    location: 'after',
                    widget: 'dxButton',
                    options: <DevExpress.ui.dxButtonOptions>{
                        text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Submit),
                        hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Submit),
                        disabled: true,
                        onInitialized: (e: any) => {
                            this.submitButton = e.component
                        },
                        onClick: (e: any) => {
                            this.onSubmit()
                        }
                    },
                }, {
                    location: 'after',
                    widget: 'dxButton',
                    options: <DevExpress.ui.dxButtonOptions>{
                        text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Cancel),
                        hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Cancel),
                        onClick: (e: any) => {
                            if (this.imrPopup != null) {
                                this.imrPopup.hide()
                            }
                        }
                    }
                }]
            }
        }

        return super.waitForOnInitialized(this.imrPopupOptions)
    }

    private setListOptions(): void {
        this.listOptions = {
            selectAllMode: "allPages",
            selectionMode: "all",
            height: '100%',
            searchMode: "contains",
            showSelectionControls: true,
            bindingOptions: {
                dataSource: '$ctrl.listDataSource',
                selectedItemKeys: '$ctrl.selectedItemKeys',
            },
            displayExpr: 'value',
            keyExpr: 'key',
            searchEnabled: true,
            searchExpr: 'value',
            onSelectionChanged: (e: any) => {
                if (this.dataSourcePrepared == true) {
                    this.submitButton.option('disabled', false)
                }
            },
            itemTemplate: (itemData: { value: string, key: OpProfileData | OpData }, itemIndex: number, itemElement: DevExpress.core.dxElement) => {
                if (itemData.key instanceof (OpProfileData)) {
                    return itemData.value
                } else {
                    return `<span>${itemData.value}</span><span style="vertical-align: super;font-size: 10px;"> ${LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Active)}</span>`
                }
            }
        }
    }

    private getSchemasToAssign(idLocation: number): JQueryPromise<[OpProfileData[], OpData[]]> {
        return this.userSession
            .getDataProvider()
            .Operator
            .getProfileAssignedSchemas(idLocation)
            .then((result: [OpProfileData[], OpData[]]) => {
                this.schemas = result

                return result
            })
    }

    private reset(): void {
        this.dataSourcePrepared = false
        this.idLocation = null
        this.selectedItemKeys = []
        this.listDataSource = []
    }

    private assignSchemasToLocation(idLocation: number, toAdd: OpProfileData[], toDel: OpData[]): DataProviderRequest<void> {
        return this.userSession.getDataProvider().Location.assignSchemasToLocation(idLocation, toAdd, toDel)
    }

    //#region IIMRLocationAssignSchemaPopup

    public showPopup(idLocation: number): void {
        this.reset()
        this.idLocation = idLocation

        if (this.imrPopup != null) {
            this.isLoading = true
            this.imrPopup.show()

            this.getSchemasToAssign(idLocation)
                .then((result: [OpProfileData[], OpData[]]) => {
                    this.listDataSource = []

                    _.forEach(result[0], (item: OpProfileData) => {
                        const value: string = item.value != null ? (item.value).toString() : LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoDataToShow)
                        this.listDataSource.push({ value: value, key: item })
                    })

                    _.forEach(result[1], (item: OpData) => {
                        const value: string = item.value != null ? (item.value).toString() : LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoDataToShow)
                        this.listDataSource.push({ value: value, key: item })
                        this.selectedItemKeys.push(item)
                    })
                })
                .always(() => {
                    this.isLoading = false

                    setTimeout(() => {
                        this.dataSourcePrepared = true
                    }, 500)
                })
        }
    }

    public hidePopup(): void {
        if (this.imrPopup != null) {
            this.imrPopup.hide();
        }
    }

    //#endregion
}
